import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, X, Loader2, Trash2, Users } from "lucide-react";
import PkiKundenakteDetail from "./PkiKundenakteDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISIKO_TONE = {
  hoch:    { chip: "bg-brand-darkred/12 text-brand-darkred border-brand-darkred/30", dot: "bg-brand-darkred", label: "Hoch" },
  mittel:  { chip: "bg-saffron/25 text-clay-dark border-saffron/50", dot: "bg-saffron", label: "Mittel" },
  niedrig: { chip: "bg-sage-dark/12 text-sage-dark border-sage-dark/30", dot: "bg-sage-dark", label: "Niedrig" },
};

const KIND_LABELS = {
  sis: "SIS",
  massnahmen: "Maßnahmenplan",
  pflegebericht: "Pflegebericht",
  kassenbrief: "Kassenbrief",
  hoehergruppe: "Höherstufung",
  qualitaet: "Qualitätscheck",
};

const SORT_OPTIONS = [
  { id: "activity", label: "Zuletzt bearbeitet" },
  { id: "name", label: "Name (A–Z)" },
  { id: "drafts", label: "Meiste Dokumente" },
];

export default function PkiKundenakte() {
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("activity");
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { customer_label, drafts_total }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pflege-ki/kundenakte/kunden`, { withCredentials: true });
      setKunden(r.data?.kunden || []);
    } catch (e) {
      toast.error("Kundenakte konnte nicht geladen werden.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const list = kunden.filter((k) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (k.customer_label || "").toLowerCase().includes(q)
          || (k.stammdaten?.notiz || "").toLowerCase().includes(q)
          || (k.stammdaten?.wohnsituation || "").toLowerCase().includes(q);
    });
    const sorted = [...list];
    if (sort === "name") sorted.sort((a, b) => (a.customer_label || "").localeCompare(b.customer_label || ""));
    else if (sort === "drafts") sorted.sort((a, b) => (b.drafts_total || 0) - (a.drafts_total || 0));
    else sorted.sort((a, b) => (b.latest_activity || "").localeCompare(a.latest_activity || ""));
    return sorted;
  }, [kunden, search, sort]);

  const deleteKunde = async (label) => {
    try {
      await axios.delete(`${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(label)}`, { withCredentials: true });
      toast.success(`Kundenakte „${label}" gelöscht.`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  if (selected) {
    return (
      <PkiKundenakteDetail
        customerLabel={selected}
        onBack={() => { setSelected(null); load(); }}
        onRenamed={(newLabel) => { setSelected(newLabel); load(); }}
        onDeleted={() => { setSelected(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="pki-kundenakte">
      {/* Header */}
      <div className="rounded-3xl border border-cream-300 bg-peach-50 p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-clay">
              Pflege-KI · Kundenakte
            </div>
            <h2 className="font-display text-2xl md:text-[28px] text-sand-800 mt-1.5 leading-tight">
              Deine Kunden — alles auf einen Blick
            </h2>
            <p className="text-sand-700 text-[13.5px] md:text-sm mt-2 max-w-3xl leading-relaxed">
              Suchen · öffnen · drucken · kopieren · bearbeiten. Pro Person zusammengeführt: SIS,
              Maßnahmenpläne, Pflegeberichte, Kassenbriefe, Höherstufungen und MD-Audits.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="pki-kundenakte-new"
            className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-5 h-11 text-[13.5px] font-semibold shadow-hover transition-colors shrink-0"
          >
            <Plus className="h-4 w-4" /> Neuer Kunde
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-sand-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nach Name, Notiz oder Wohnsituation suchen …"
            data-testid="pki-kundenakte-search"
            className="w-full rounded-xl border border-cream-300 bg-white pl-9 pr-3 py-2.5 text-[13px] text-sand-800 focus:outline-none focus:border-terra"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="pki-kundenakte-sort"
            className="rounded-xl border border-cream-300 bg-white px-3 py-2.5 text-[12.5px] text-sand-800"
          >
            {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <div className="text-[12px] text-sand-600 whitespace-nowrap">
            {loading ? "lädt …" : `${filtered.length} von ${kunden.length}`}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {!loading && kunden.length === 0 && (
        <div
          className="rounded-3xl border border-cream-300 bg-white p-10 text-center"
          data-testid="pki-kundenakte-empty"
        >
          <div className="mx-auto h-14 w-14 rounded-full bg-peach-50 flex items-center justify-center mb-4">
            <Users className="h-7 w-7 text-terra" />
          </div>
          <h3 className="font-display text-xl text-sand-800 mb-2">Noch keine Kunden angelegt</h3>
          <p className="text-sand-700 text-[13.5px] max-w-md mx-auto">
            Leg deinen ersten Kunden mit ein paar Stammdaten an. Danach kannst du für ihn SIS,
            Maßnahmenpläne und weitere Dokumente generieren.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="pki-kundenakte-new-empty"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-5 h-11 text-[13.5px] font-semibold"
          >
            <Plus className="h-4 w-4" /> Ersten Kunden anlegen
          </button>
        </div>
      )}

      {/* Kunden-Grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pki-kundenakte-list">
          {filtered.map((k) => {
            const t = RISIKO_TONE[k.latest_score_label] || null;
            const kindTotal = k.drafts_total || 0;
            return (
              <div
                key={k.customer_label}
                data-testid={`pki-kundenakte-row-${k.customer_label}`}
                className="group relative rounded-2xl border border-cream-300 bg-white hover:border-brand-rose hover:shadow-hover transition-all overflow-hidden"
              >
                <button
                  onClick={() => setSelected(k.customer_label)}
                  className="w-full text-left p-5"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-display text-lg text-sand-800 leading-tight truncate">
                        {k.customer_label}
                      </div>
                      {k.stammdaten && (k.stammdaten.alter || k.stammdaten.pflegegrad) && (
                        <div className="text-[12px] text-sand-600 mt-0.5">
                          {k.stammdaten.alter ? `${k.stammdaten.alter} Jahre` : ""}
                          {k.stammdaten.alter && k.stammdaten.pflegegrad ? " · " : ""}
                          {k.stammdaten.pflegegrad ? `PG ${k.stammdaten.pflegegrad}` : ""}
                        </div>
                      )}
                    </div>
                    {t && (
                      <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full border text-[10.5px] uppercase tracking-[0.16em] font-semibold shrink-0 ${t.chip}`}>
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} />
                        {t.label}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[12px] text-sand-700 mt-3">
                    <Stat label="Entwürfe" value={kindTotal} />
                    <Stat label="MD-Audits" value={k.audits_total || 0} />
                    <Stat label="Score" value={typeof k.latest_score === "number" ? k.latest_score : "–"} />
                  </div>
                  {kindTotal > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {Object.entries(k.drafts_by_kind || {}).map(([kind, n]) => (
                        <span key={kind} className="text-[10px] uppercase tracking-[0.16em] font-semibold px-2 h-5 rounded-full bg-cream-100 border border-cream-300 text-sand-700 inline-flex items-center">
                          {KIND_LABELS[kind] || kind} · {n}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 text-[11px] text-sand-500">
                    Letzte Aktivität: {fmtDate(k.latest_activity)}
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(k); }}
                  data-testid={`pki-kundenakte-delete-${k.customer_label}`}
                  aria-label="Kunde löschen"
                  className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/80 hover:bg-brand-darkred hover:text-white text-sand-500 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateKundeModal
          onClose={() => setShowCreate(false)}
          onCreated={(newLabel) => {
            setShowCreate(false);
            load();
            toast.success(`Kunde „${newLabel}" angelegt.`);
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          kunde={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteKunde(confirmDelete.customer_label)}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-cream-300 bg-cream-50 px-2 py-1.5">
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-sand-500 font-semibold">{label}</div>
      <div className="font-display text-base text-sand-800 leading-tight mt-0.5">{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

// ---------------------------------------------------------------------------
// Modal: Neuen Kunden anlegen
// ---------------------------------------------------------------------------
function CreateKundeModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_label: "",
    alter: "",
    pflegegrad: "",
    wohnsituation: "",
    notiz: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        customer_label: form.customer_label.trim(),
        alter: form.alter ? Number(form.alter) : null,
        pflegegrad: form.pflegegrad ? Number(form.pflegegrad) : null,
        wohnsituation: form.wohnsituation.trim() || null,
        notiz: form.notiz.trim() || null,
      };
      const r = await axios.post(`${API}/pflege-ki/kundenakte/kunden`, payload, { withCredentials: true });
      onCreated?.(r.data.customer_label);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "TRIAL_AKTEN_LIMIT") {
        toast.error(`${detail.title}: ${detail.message}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Anlegen fehlgeschlagen.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-sand-800/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-hover max-w-lg w-full p-7"
        data-testid="pki-kunde-create-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
              Kundenakte
            </div>
            <h2 className="font-display text-2xl text-sand-800 mt-1">Neuen Kunden anlegen</h2>
            <p className="text-[12.5px] text-sand-600 mt-1">
              Stammdaten sind optional — nur die Bezeichnung ist Pflicht.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sand-500 hover:text-sand-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Bezeichnung / Name * (anonymisiert)" hint={'z. B. "Frau M., 82" – niemals Klarnamen'}>
            <input
              type="text" required minLength={2} value={form.customer_label}
              onChange={(e) => setForm({ ...form, customer_label: e.target.value })}
              data-testid="pki-kunde-label"
              placeholder="Frau M., 82"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Alter">
              <input
                type="number" min="0" max="130" value={form.alter}
                onChange={(e) => setForm({ ...form, alter: e.target.value })}
                data-testid="pki-kunde-alter"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
              />
            </Field>
            <Field label="Pflegegrad">
              <select
                value={form.pflegegrad}
                onChange={(e) => setForm({ ...form, pflegegrad: e.target.value })}
                data-testid="pki-kunde-pflegegrad"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2.5 text-sm"
              >
                <option value="">–</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>PG {n}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Wohnsituation">
            <input
              type="text" value={form.wohnsituation}
              onChange={(e) => setForm({ ...form, wohnsituation: e.target.value })}
              data-testid="pki-kunde-wohn"
              placeholder="z. B. Eigene Wohnung, Angehörige im Haus"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </Field>
          <Field label="Notiz (nur intern)">
            <textarea
              rows={3} value={form.notiz}
              onChange={(e) => setForm({ ...form, notiz: e.target.value })}
              data-testid="pki-kunde-notiz"
              placeholder="Kurze interne Notiz zur Person"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra resize-none"
            />
          </Field>
        </div>

        <div className="mt-6 flex gap-3 justify-end border-t border-cream-300 pt-5">
          <button type="button" onClick={onClose}
            className="rounded-full px-5 h-10 text-[13px] font-semibold text-sand-700 hover:bg-cream-100">
            Abbrechen
          </button>
          <button type="submit" disabled={busy}
            data-testid="pki-kunde-create-submit"
            className="rounded-full bg-terra hover:bg-terra-dark text-white px-6 h-10 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Kunde anlegen
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-sand-500 mt-1">{hint}</div>}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Modal: Kunde löschen (Bestätigung)
// ---------------------------------------------------------------------------
function ConfirmDeleteModal({ kunde, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const total = (kunde.drafts_total || 0) + (kunde.audits_total || 0);
  return (
    <div className="fixed inset-0 z-[110] bg-sand-800/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="pki-kunde-delete-modal"
        className="bg-white rounded-3xl shadow-hover max-w-md w-full p-7">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">
          Löschen bestätigen
        </div>
        <h2 className="font-display text-2xl text-sand-800 mb-3">
          „{kunde.customer_label}&ldquo; wirklich löschen?
        </h2>
        <p className="text-[13.5px] text-sand-700 leading-relaxed">
          Es werden <strong>{kunde.drafts_total || 0} Entwürfe</strong>,{" "}
          <strong>{kunde.audits_total || 0} MD-Audits</strong> und die Stammdaten
          unwiderruflich gelöscht.
          {total > 0 && (
            <span className="block mt-2 text-[12.5px] text-clay">
              Dieser Vorgang kann nicht rückgängig gemacht werden.
            </span>
          )}
        </p>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onCancel}
            className="rounded-full px-5 h-10 text-[13px] font-semibold text-sand-700 hover:bg-cream-100">
            Abbrechen
          </button>
          <button
            onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
            disabled={busy}
            data-testid="pki-kunde-delete-confirm"
            className="rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white px-6 h-10 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Endgültig löschen
          </button>
        </div>
      </div>
    </div>
  );
}
